import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Circle } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { AngleUnderline } from './Landing/angle-underline';
import type { OnboardingActionProgress, OnboardingProgress } from '../types/onboarding';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { cn } from '../lib/utils';

type OnboardingBubbleContentProps = {
  progress: OnboardingProgress;
};

const dmMonoStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
};

type AchievementRowProps = {
  action: OnboardingActionProgress;
  label: string;
};

const AchievementRow: React.FC<AchievementRowProps> = ({ action, label }) => (
  <li className="flex items-start gap-2 text-xs whitespace-nowrap">
    {action.completed ? (
      <Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[var(--scribane-green)]" />
    ) : (
      <Circle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
    )}
    <span className={cn(action.completed && 'text-muted-foreground line-through')}>
      {label}
      <span className="text-muted-foreground"> (+{action.points})</span>
    </span>
  </li>
);

export const OnboardingBubbleContent: React.FC<OnboardingBubbleContentProps> = ({ progress }) => {
  const { t } = useLocale();
  const [completedOpen, setCompletedOpen] = useState(false);
  const hasContent = progress.actions.length > 0 || progress.milestones.length > 0;
  const nextMilestoneIndex = progress.milestones.findIndex((milestone) => !milestone.granted);

  const { pendingActions, completedActions } = useMemo(() => {
    const pending: OnboardingActionProgress[] = [];
    const completed: OnboardingActionProgress[] = [];
    for (const action of progress.actions) {
      if (action.completed) {
        completed.push(action);
      } else {
        pending.push(action);
      }
    }
    return { pendingActions: pending, completedActions: completed };
  }, [progress.actions]);

  return (
    <div className="inline-flex w-max max-w-[min(100vw-2rem,22rem)] flex-col gap-4">
      <p
        className="relative inline-block text-sm font-normal uppercase tracking-[0.12em] pb-2"
        style={dmMonoStyle}
      >
        <span className="relative z-10">{t('onboarding_title')}</span>
        <AngleUnderline
          size="thin"
          className="left-[0.04em] right-[0.04em] -bottom-0.5 h-2"
        />
      </p>

      {!hasContent ? (
        <p className="text-xs text-muted-foreground">{t('onboarding_empty')}</p>
      ) : (
        <>
          {progress.actions.length > 0 && (
            <div>
              <p
                className="text-xs font-normal uppercase tracking-[0.1em] mb-2"
                style={dmMonoStyle}
              >
                {t('onboarding_actions_heading')}
              </p>
              {completedActions.length > 0 && (
                <Collapsible
                  open={completedOpen}
                  onOpenChange={setCompletedOpen}
                  className="mb-2"
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center gap-1 text-left text-xs text-muted-foreground hover:text-foreground"
                    >
                      <ChevronDown
                        className={cn(
                          'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                          !completedOpen && '-rotate-90'
                        )}
                      />
                      <span>
                        {t('onboarding_completed_achievements').replace(
                          '{count}',
                          String(completedActions.length)
                        )}
                      </span>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="mt-1.5 flex flex-col gap-1.5">
                      {completedActions.map((action) => (
                        <AchievementRow
                          key={action.id}
                          action={action}
                          label={t(`onboarding_action_${action.id}`)}
                        />
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              )}
              <ul className="flex flex-col gap-1.5">
                {pendingActions.map((action) => (
                  <AchievementRow
                    key={action.id}
                    action={action}
                    label={t(`onboarding_action_${action.id}`)}
                  />
                ))}
              </ul>
            </div>
          )}

          {progress.milestones.length > 0 && (
            <div>
              <p
                className="text-xs font-normal uppercase tracking-[0.1em] mb-3"
                style={dmMonoStyle}
              >
                {t('onboarding_roadmap_heading')}
              </p>
              <div className="relative">
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-[11px] top-[10px] bottom-[10px] w-[2px] rounded-full"
                  style={{ backgroundColor: 'var(--scribane-green)' }}
                />
                <ol className="relative m-0 list-none p-0">
                  {progress.milestones.map((milestone, index) => {
                    const isCurrent = index === nextMilestoneIndex;
                    const isCurrentActive = isCurrent && progress.points > 0;
                    const isPast = milestone.granted;

                    return (
                      <li
                        key={milestone.id}
                        className="relative z-[1] flex items-center gap-3 py-1.5 first:pt-0 last:pb-0"
                      >
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center bg-popover">
                          {isPast ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--scribane-green)] text-white">
                              <Check className="h-3 w-3" strokeWidth={3} />
                            </span>
                          ) : isCurrentActive ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-[var(--scribane-green)] bg-popover">
                              <span className="h-2 w-2 rounded-full bg-[var(--scribane-green)]" />
                            </span>
                          ) : (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border bg-popover">
                              <Circle className="h-2.5 w-2.5 text-muted-foreground" />
                            </span>
                          )}
                        </div>
                        <span
                          className={cn(
                            'text-xs whitespace-nowrap',
                            isPast && 'text-muted-foreground',
                            isCurrentActive && 'font-medium text-foreground'
                          )}
                        >
                          {t(`onboarding_milestone_${milestone.id}`)}
                          <span className="text-muted-foreground">
                            {' '}
                            {t('onboarding_roadmap_at_points').replace(
                              '{points}',
                              String(milestone.pointsRequired)
                            )}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
